// Settings → Connected apps & AI: the AI apps (OAuth connections, e.g. Claude) and personal access tokens that may
// reach Bloom as you, with what each may do. Edit or revoke any of them (revoking ends its access at once), and make
// tokens for scripts. See docs/connector.md.
import { useEffect, useState } from "react";
import { PAT_DAYS, type ConnectorGrant, type ConnectorServerChoice, type CreatedToken, type GrantServerScope, type UserSettings } from "@bloom/shared";
import { api } from "../api.ts";
import { describeScope, initialScopes, ScopePicker, scopesIncomplete } from "./ConnectorScopes.tsx";
import { colorFor, initials } from "./ui.tsx";
import "./connector.css";

const day = (ts: number) => new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function ago(ts: number | null): string {
  if (!ts) return "Never used";
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 90) return "Used just now";
  if (s < 3600) return `Used ${Math.round(s / 60)} min ago`;
  if (s < 86400) return `Used ${Math.round(s / 3600)} h ago`;
  return `Used ${day(ts)}`;
}

export function ConnectedApps() {
  const [grants, setGrants] = useState<ConnectorGrant[] | null>(null);
  const [servers, setServers] = useState<ConnectorServerChoice[]>([]);
  const [making, setMaking] = useState(false);
  const [made, setMade] = useState<CreatedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    Promise.all([api<ConnectorGrant[]>("/connector/grants"), api<ConnectorServerChoice[]>("/connector/choices")]).then(
      ([g, s]) => {
        setGrants(g);
        setServers(s);
      },
      (e: Error) => setError(e.message),
    );
  useEffect(() => void load(), []);
  const url = `${window.location.origin}/mcp`;
  const ai = grants?.filter((g) => g.kind === "oauth") ?? [];
  const tokens = grants?.filter((g) => g.kind === "pat") ?? [];

  return (
    <div className="cx">
      <div className="setting-row cx-intro">
        <div className="setting-text">
          <div className="setting-label">Connect an AI to Bloom</div>
          <div className="setting-help">
            Add Bloom as a custom connector in Claude (Settings → Connectors → Add custom connector) or any app that speaks MCP, with this address. You'll
            choose what it may do when it asks.
          </div>
          <CopyField value={url} label="Connector URL" />
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <DmAiSetting />

      <h3 className="cx-head">AI apps</h3>
      {grants && !ai.length && <p className="muted cx-none">Nothing connected yet.</p>}
      <ul className="cx-list">
        {ai.map((g) => (
          <GrantItem key={g.id} grant={g} servers={servers} onChanged={load} />
        ))}
      </ul>

      <div className="cx-head-row">
        <h3 className="cx-head">Personal access tokens</h3>
        {!making && !made && (
          <button type="button" className="btn btn-small" onClick={() => setMaking(true)}>
            New token
          </button>
        )}
      </div>
      <p className="setting-help cx-sub">For scripts and agents that can't sign in with OAuth: send it as “Authorization: Bearer …” to the connector URL. It works like a connection, with the same choices.</p>
      {made && (
        <div className="cx-made" role="status">
          <div className="setting-label">Copy “{made.grant.name}” now: you won't see it again.</div>
          <CopyField value={made.token} label="Token" secret />
          <button type="button" className="btn btn-small" onClick={() => setMade(null)}>Done</button>
        </div>
      )}
      {making && (
        <NewToken
          servers={servers}
          onCancel={() => setMaking(false)}
          onMade={(t) => {
            setMaking(false);
            setMade(t);
            void load();
          }}
        />
      )}
      {grants && !tokens.length && !making && <p className="muted cx-none">No tokens.</p>}
      <ul className="cx-list">
        {tokens.map((g) => (
          <GrantItem key={g.id} grant={g} servers={servers} onChanged={load} />
        ))}
      </ul>
    </div>
  );
}

function CopyField({ value, label, secret }: { value: string; label: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="cx-copy">
      <input className="input cx-copy-input" aria-label={label} readOnly value={value} onFocus={(e) => e.currentTarget.select()} spellCheck={false} data-secret={secret ? "1" : undefined} />
      <button
        type="button"
        className="btn btn-small"
        onClick={async () => {
          await navigator.clipboard?.writeText(value).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function GrantItem({ grant, servers, onChanged }: { grant: ConnectorGrant; servers: ConnectorServerChoice[]; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [scopes, setScopes] = useState<GrantServerScope[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expired = grant.expiresAt !== null && grant.expiresAt < Date.now();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="cx-item" data-grant={grant.name}>
      <div className="cx-item-head">
        <span className={`cx-icon${grant.kind === "pat" ? " pat" : ""}`} style={grant.kind === "oauth" ? { background: colorFor(grant.id) } : undefined} aria-hidden="true">
          {grant.kind === "oauth" ? initials(grant.name) : "⌘"}
        </span>
        <div className="cx-item-text">
          <div className="cx-name">
            {grant.name}
            {grant.host && <span className="cx-host">{grant.host}</span>}
          </div>
          <div className="cx-meta">
            {grant.kind === "pat" ? `Made ${day(grant.createdAt)} · ${expired ? "Expired" : `Expires ${day(grant.expiresAt!)}`}` : `Connected ${day(grant.createdAt)}`} · {ago(grant.lastUsedAt)}
          </div>
        </div>
        {!editing && !confirming && (
          <div className="cx-item-actions">
            <button
              type="button"
              className="btn btn-small"
              onClick={() => {
                setScopes(initialScopes(servers, grant.servers.map(({ serverName: _n, ...scope }) => scope)));
                setEditing(true);
              }}
            >
              Edit
            </button>
            <button type="button" className="btn btn-small btn-danger" onClick={() => setConfirming(true)}>
              Revoke
            </button>
          </div>
        )}
      </div>
      {!editing && (
        <ul className="cx-scopes">
          {grant.servers.length ? (
            grant.servers.map((s) => (
              <li key={s.serverId}>
                <strong>{s.serverName}</strong> {describeScope(s, servers.find((x) => x.id === s.serverId)?.apps)}
              </li>
            ))
          ) : (
            <li className="muted">No servers: it can't reach anything.</li>
          )}
        </ul>
      )}
      {confirming && (
        <div className="cx-confirm" role="alertdialog" aria-label={`Revoke ${grant.name}`}>
          <span>{grant.kind === "pat" ? "Revoke this token? Scripts using it stop working at once." : `Revoke ${grant.name}? It loses access at once and has to ask again.`}</span>
          <div className="row">
            <button type="button" className="btn btn-small" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn-small btn-danger" disabled={busy} onClick={() => void run(() => api(`/connector/grants/${grant.id}`, { method: "DELETE" }))}>
              {busy ? "Revoking…" : "Revoke"}
            </button>
          </div>
        </div>
      )}
      {editing && (
        <div className="cx-edit">
          <ScopePicker servers={servers} value={scopes} onChange={setScopes} idPrefix={`g-${grant.id}`} />
          <div className="row">
            <button type="button" className="btn btn-small" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="btn btn-small btn-primary"
              disabled={busy || scopesIncomplete(scopes)}
              onClick={() => void run(async () => {
                await api(`/connector/grants/${grant.id}`, { method: "PATCH", body: { servers: scopes } });
                setEditing(false);
              })}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </li>
  );
}

function NewToken({ servers, onCancel, onMade }: { servers: ConnectorServerChoice[]; onCancel: () => void; onMade: (t: CreatedToken) => void }) {
  const [name, setName] = useState("");
  const [days, setDays] = useState<number>(30);
  const [scopes, setScopes] = useState<GrantServerScope[]>(() => initialScopes(servers, null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="cx-new"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          onMade(await api<CreatedToken>("/connector/tokens", { body: { name: name.trim(), days, servers: scopes } }));
        } catch (err) {
          setError(err instanceof Error ? err.message : "Couldn't make it.");
          setBusy(false);
        }
      }}
    >
      <div className="cx-new-row">
        <div className="field">
          <label htmlFor="cx-name">Name</label>
          <input id="cx-name" className="input" value={name} maxLength={32} placeholder="Nightly script" onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label htmlFor="cx-days">Expires after</label>
          <select id="cx-days" className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {PAT_DAYS.map((d) => (
              <option key={d} value={d}>{d === 365 ? "1 year" : `${d} days`}</option>
            ))}
          </select>
        </div>
      </div>
      <ScopePicker servers={servers} value={scopes} onChange={setScopes} idPrefix="cx-new" />
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row">
        <button type="button" className="btn btn-small" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn btn-small btn-primary" disabled={busy || !name.trim() || !scopes.length || scopesIncomplete(scopes)}>
          {busy ? "Making…" : "Make token"}
        </button>
      </div>
    </form>
  );
}

/** Other people's AIs in my DMs (UserSettings.dmAi): off, no one else's AI reads a DM I'm in. */
function DmAiSetting() {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    api<UserSettings>("/me/settings").then((s) => setOn(s.dmAi !== false), () => setOn(true));
  }, []);
  return (
    <div className="setting-row">
      <div className="setting-text">
        <div className="setting-label">AIs of people I DM with</div>
        <div className="setting-help">
          Someone can let their own AI read their DMs. Off, no one else's AI reads a DM you're in. AIs never send in DMs either way. Saved to your account.
        </div>
        <label className="us-switch">
          <input
            type="checkbox"
            className="us-check"
            checked={on ?? true}
            disabled={on === null}
            onChange={(e) => {
              const next = e.target.checked;
              setOn(next);
              void api("/me/settings", { method: "PUT", body: { dmAi: next } }).catch(() => setOn(!next));
            }}
          />
          <span>Let their AIs read our DMs</span>
        </label>
      </div>
    </div>
  );
}
